import AwesomeDebouncePromise from 'awesome-debounce-promise';
import axios from 'axios';
import classNames from 'classnames';
import { SERVER_URL } from 'Constants';
import { saveAs } from 'file-saver';
import { GqlUserEntityList, requestUsers } from 'Hooks/Api/apiUsers';
import { OrganisationEntity, UserEntity } from 'Models/Entities';
import { userTypeOptions } from 'Models/Enums';
import { OrganisationHelper } from 'Models/Helpers/OrganisationHelper';
import { store } from 'Models/Store';
import moment from 'moment';
import { useState } from 'react';
import { buildBlob } from 'Util/CsvUtils';
import HandleEvents from 'Util/HandleEvents';
import { booleanToActive } from 'Util/StringUtils';
import alertToast from 'Util/ToastifyUtils';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { ComboboxOption } from 'Views/Components/Combobox/Combobox';
import MultiComboboxSetter from 'Views/Components/Combobox/MultiComboboxSetter';
import EntityList from 'Views/Components/EntityList/EntityList';
import MeatballMenu from 'Views/Components/EntityList/MeatballMenu';
import If from 'Views/Components/If/If';
import { confirmModal } from 'Views/Components/Modal/ModalUtils';
import TablePagination from 'Views/Components/Pagination/TablePagination';
import RequestWrap, { GetRefreshKey } from 'Views/Components/RequestWrap/RequestWrap';
import { TextFieldSetter } from 'Views/Components/TextBox/TextFieldSetter';

// Method to fetch and return the UserEntitys based on the page number and sorting
const fetchUsers = AwesomeDebouncePromise(async (
	search: string,
	sortColumn: string,
	sortDescending: boolean,
	page: number,
	filterByOrgIds?: string[],
	filterByBusinessEntityIds?: string[],
	noPagination?: boolean
) => {
	const response = await requestUsers({
		search,
		sortColumn,
		sortDescending,
		page,
		filterByOrgIds,
		filterByBusinessEntityIds,
		noPagination,
	});

	return response.data;

}, 300, { leading: true });

// Fetch organisations options. Used for organisation ID filter
const fetchOrganisationOptions = AwesomeDebouncePromise(async (
	search?: string,
) => {
	const organisations = await OrganisationEntity.fetchOrganisationList(search) ?? [];
	return organisations
		.map(x => {
			return {
				display: x.primaryBusinessEntity?.name,
				value: x.id,
			};
		}) as ComboboxOption<string>[];
}, 300);

const UsersList = () => {
	// For the search bar display
	const [search, setSearch] = useState('');
	const [sortColumn, setSortColumn] = useState('firstName lastName');
	const [sortDescending, setSortDescending] = useState(false);
	const [page, setPage] = useState(0);
	const [showFilters, setShowFilters] = useState(false);
	const [businessEntityIds, setBusinessEntityIds] = useState([] as string[]);
	const [organisationIds, setOrganisationIds] = useState([] as string[]);
	const [triggerUpdate, setTriggerUpdate] = useState(0); // Increment to force the user list to refresh

	/**
	 * Callback for sorting changes
	 * @param header The name of the sorting column
	 */
	const onSortChange = (header: string) => {
		if (sortColumn === header) {
			setSortDescending(!sortDescending);
		} else {
			setSortDescending(false);
		}

		setSortColumn(header);
	};

	const changeIsActive = async (user: UserEntity, active: boolean) => {
		// If you're deactivating the last organisation manager, we prevent this
		if (!active && await user.isLastOrganisationManager()) {
			alertToast('You cannot deactivate the last organisation manager in an organisation', 'error');
			return;
		}

		const deactivateSelf = !active && user.id === store.userId;
		try {
			if (!active) {
				await confirmModal(
					`Deactivate ${deactivateSelf ? 'your account?' : 'user?'}`,
					deactivateSelf
						? "If you deactivate your account, you will be logged out, and won't be able to log in again."
						: 'If you deactivate this user, they will be unable to login to the Access Intell platform.',
				);
			} else {
				await confirmModal(
					'Activate user?',
					'If you activate this user, they will be able to login to the Access platform again.',
				);
			}
		} catch {
			return;
		}

		try {
			await axios.post(`${SERVER_URL}/api/entity/UserEntity/updateFields`, {
				id: user.id,
				isActive: active,
			});
		} catch (e: any) {
			alertToast(`You do not have access to ${active ? 'activate' : 'deactivate'} this user`, 'error');
			return;
		}

		if (deactivateSelf) {
			store.routerHistory.push('/logout');
		} else {
			alertToast(`User ${active ? 'activated' : 'deactivated'}`, 'success');

			// Refresh the list, so we see the change
			setTriggerUpdate(x => x + 1);
		}
	};

	const organisationBusinessColumn = store.userType === 'SUPER_USER'
		? {
			displayName: 'Organisation',
			columnName: 'organisation.primaryBusinessEntity.name',
			value: (user: UserEntity) => user.organisation?.primaryBusinessEntity?.name,
			sortable: true,
			sortClicked: () => {
				onSortChange('organisation.primaryBusinessEntity.name');
			},
			className: 'field-organisation',
		}
		: {
			displayName: 'Business Entities',
			columnName: 'businessEntities',
			value: (user: UserEntity) => user.businessEntitys?.map(x => x.businessEntity.name).join(', '),
			sortable: false,
			className: 'field-business-entities',
		};

	const fetchUsersForExport = async () => {
		const response = await requestUsers({
			search,
			sortColumn,
			sortDescending,
			page,
			filterByOrgIds: organisationIds,
			filterByBusinessEntityIds: businessEntityIds,
			noPagination: true,
		});
		const users = response.data.userEntitys.map((user: any) => new UserEntity(user)) as UserEntity[];

		const result = [] as string[][];
		const columns = [
			{
				displayName: "Organisation",
				value: (user: UserEntity) => user.organisation?.primaryBusinessEntity?.name,
				isAccessible: store.userType === 'SUPER_USER',
			},
			{
				displayName: "Business Entities",
				value: (user: UserEntity) => user.businessEntitys?.map(x => x.businessEntity.name).join(', '),
				isAccessible: true
			},
			{
				displayName: "User Id",
				value: (user: UserEntity) => user.id,
				isAccessible: store.userType === 'SUPER_USER',
			},
			{
				displayName: "First Name",
				value: (user: UserEntity) => user.firstName,
				isAccessible: true
			},
			{
				displayName: "Last Name",
				value: (user: UserEntity) => user.lastName,
				isAccessible: true
			},
			{
				displayName: "Email",
				value: (user: UserEntity) => user.email,
				isAccessible: true
			},
			{
				displayName: "Phone Number",
				value: (user: UserEntity) => user.phone,
				isAccessible: true
			},
			{
				displayName: "User Type",
				value: (user: UserEntity) => userTypeOptions[user.userType],
				isAccessible: true
			},
			{
				displayName: "Status",
				value: (user: UserEntity) => booleanToActive(user.isActive),
				isAccessible: true
			},
			{
				displayName: "Enable Notification",
				value: (user: UserEntity) => user.enableEmailNotifications?.toString().toUpperCase() ?? "FALSE",
				isAccessible: true
			},
			{
				displayName: "Access Approve",
				value: (user: UserEntity) => user.canAccessApprove?.toString().toUpperCase() ?? "FALSE",
				isAccessible: true
			},
			{
				displayName: "Access PPSR",
				value: (user: UserEntity) => user.canAccessPPSR?.toString().toUpperCase() ?? "FALSE",
				isAccessible: true
			},
			{
				displayName: "Access Monitor",
				value: (user: UserEntity) => user.canAccessIntel?.toString().toUpperCase() ?? "FALSE",
				isAccessible: true
			},
			{
				displayName: "Credit Approval Limit",
				value: (user: UserEntity) => user.creditApprovalLimit?.toString() ?? "0",
				isAccessible: true
			},
			{
				displayName: "Legacy Id",
				value: (user: UserEntity) => user.legacyID?.toString(),
				isAccessible: store.userType === 'SUPER_USER',
			},
		];

		const headers = columns.filter(x => x.isAccessible).map(x => x.displayName);
		result.push(headers);
		users.forEach(user => {
			const row = columns.filter(x => x.isAccessible).map(x => x.value(user) ?? "");
			result.push(row);
		});

		return result;
	}

	const handleExportCsv = async () => {
		const exportUsers = await fetchUsersForExport();
		const blob = buildBlob(exportUsers, ',', '"');
		saveAs(blob, `exported-users-${moment().format('YYYY-MM-DD')}.csv`);
	};

	return (
		<div className="users-list">
			<div className="search-container">
				<div className="searchbar search">
					<TextFieldSetter
						className={classNames('search-input search__collection')}
						value={search}
						setValue={setSearch}
						onAfterChange={() => setPage(0)}
						label="A search for entities"
						labelVisible={false}
						placeholder="Search by Name or Email"
					/>

					<Button
						className={classNames('filter-toggle-btn', { active: showFilters })}
						icon={{ icon: 'filter-2', iconPos: 'icon-left' }}
						colors={Colors.Primary}
						display={showFilters ? Display.Solid : Display.Outline}
						onClick={() => {
							setShowFilters(show => !show);
							setBusinessEntityIds([]);
							setOrganisationIds([]);
						}}
					>
						Filter By
					</Button>
				</div>
				{store.userPermissions.commonManageUsers !== 'SELF' && (
					<div className='actions'>
						<div className="new-user">
							<Button
								className="new-user-btn"
								icon={{ icon: 'plus-2', iconPos: 'icon-left' }}
								display={Display.Solid}
								colors={Colors.Primary}
								{...HandleEvents(() => store.routerHistory.push('/hub/users/new'))}
							>
								New User
							</Button>
						</div>
						<Button
							className="export-button"
							colors={Colors.Primary}
							display={Display.Outline}
							onClick={handleExportCsv}
						>
							Export CSV
						</Button>
					</div>
				)}
				<If condition={showFilters}>
					<div className="search-filter-section">
						<If condition={store.userType === 'SUPER_USER'}>
							<MultiComboboxSetter
								className="organisation-filter"
								value={organisationIds}
								setValue={setOrganisationIds}
								getOptionValue={(value?: string) => value}
								placeholder="Clients"
								label=""
								labelVisible={false}
								searchable
								initialOptions={fetchOrganisationOptions}
								options={fetchOrganisationOptions}
								onAfterSet={() => setPage(0)}
							/>
						</If>
						<If condition={store.userType !== 'SUPER_USER'}>
							<MultiComboboxSetter
								className="business-entity-filter"
								value={businessEntityIds}
								setValue={setBusinessEntityIds}
								getOptionValue={(value?: string) => value}
								placeholder="Business entities"
								label=""
								labelVisible={false}
								searchable
								onAfterSet={() => setPage(0)}
								options={OrganisationHelper.getBusinessOptions(store.getUser?.organisation)}
							/>
						</If>
					</div>
				</If>
			</div>
			<div className="collection-component">
				{/* Component to fetch and render the User List */}
				<RequestWrap
					request={() => fetchUsers(
						search,
						sortColumn,
						sortDescending,
						page,
						organisationIds,
						businessEntityIds,
					)}
					refreshKey={GetRefreshKey(
						search,
						sortColumn,
						sortDescending,
						page,
						businessEntityIds,
						organisationIds,
						triggerUpdate,
					)}
				>
					{(response: GqlUserEntityList) => {

						const users = response.userEntitys.map((user: any) => new UserEntity(user)) as UserEntity[];

						return (
							<>
								<EntityList
									collection={users}
									columns={[
										{
											displayName: 'Name',
											columnName: 'firstName lastName',
											value: user => `${user.firstName} ${user.lastName}`,
											sortable: true,
											sortClicked: () => {
												onSortChange('firstName lastName');
											},
											className: 'field-name',
										},
										{
											displayName: 'Email Address',
											columnName: 'email',
											value: user => user.email,
											sortable: true,
											sortClicked: () => {
												onSortChange('email');
											},
											className: 'field-email',
										},
										organisationBusinessColumn,
										{
											displayName: 'User Type',
											columnName: 'userType',
											value: user => userTypeOptions[user.userType],
											sortable: true,
											sortClicked: () => {
												onSortChange('userType');
											},
											className: 'field-user-type',
										},
										{
											displayName: 'Status',
											columnName: 'isActive',
											value: user => booleanToActive(user.isActive),
											sortable: true,
											sortClicked: () => {
												onSortChange('isActive');
											},
											className: 'field-status',
										},
										{
											displayName: '',
											columnName: 'meatball-menu',
											value: user => (
												<MeatballMenu actions={[
													{
														display: 'Activate user',
														action: () => changeIsActive(user, true),
														visible: !user.isActive && user.canEdit,
													},
													{
														display: 'Deactivate user',
														action: () => changeIsActive(user, false),
														visible: user.isActive && user.canEdit,
													},
												]}
												/>
											),
											sortable: false,
											className: 'meatball-menu',
											hide: store.userPermissions.commonManageUsers === 'SELF',
										},
									]}
									idColumn="id"
									sortColumn={sortColumn}
									sortDescending={sortDescending}
									onClickRow={user => {
										store.routerHistory.push(`/hub/users/${user.id}`);
									}}
									rowClassName={user => classNames({ disabled: !user.isActive })}
								/>
								<section className="collection__load">
									<TablePagination
										perPage={MAX_USERS}
										pageNo={page}
										totalRecords={response.countUserEntitys.number}
										onPageChange={setPage}
									/>
								</section>
							</>
						)
					}}
				</RequestWrap>
			</div>
		</div>
	);
};
export default UsersList;

export const MAX_USERS = 10;
